// The cells the Usage tables share: the origin label, the stage bar, the
// usage measure, the deal status and the coverage line.

import { FC, ReactNode } from "react";
import { Link } from "react-router-dom";
import { clsx } from "clsx";
import {
    Button,
    DataTableActionButton,
    DataTableActions,
    DataTableEntityCell,
    StatusDot,
    StatusDotColor,
    Tag,
    Tooltip,
    message,
} from "../../../../design-system";
import { getColorFromLetter } from "../../../../common/Color";
import { MeasureProgress } from "../../../negotiations/v2/list/components/DealSavings";
import {
    DEAL_STATUS_LABEL,
    DealStatus,
    STAGES,
    stageLabel,
    utilizationTier,
} from "../model/derived";
import { isDrillableProject } from "../model/fixtures";
import { longDate, pct } from "../model/format";
import { setItemStatus } from "../model/store";
import type {
    ActionItem,
    Coverage,
    Origin,
    UsageDeal,
    UsageProject,
    UsageVendor,
} from "../model/types";
import { usageProjectPath } from "../paths";
import classes from "./cells.module.less";

export const ORIGIN_LABEL: Record<Origin, string> = {
    requested: "You requested",
    renewal: "Renewal approaching",
    identified: "Identified by us",
};

export const OriginTag: FC<{ origin: Origin }> = ({ origin }) => (
    <Tag className={clsx(classes.origin, classes[`origin--${origin}`])}>{ORIGIN_LABEL[origin]}</Tag>
);

const STAGE_COLOR: Record<UsageProject["stage"], StatusDotColor> = {
    proposed: "orange",
    usage_analysis: "blue",
    playbook: "blue",
    negotiating: "orange",
    closed: "green",
};

/** The project's stage as a dot and label over a bar of the four stages. */
export const UsageStageCell: FC<{ project: UsageProject }> = ({ project }) => {
    const reached = STAGES.indexOf(project.stage);
    const color: StatusDotColor =
        project.stage === "usage_analysis" && project.analyses.length === 0
            ? "grey"
            : STAGE_COLOR[project.stage];
    return (
        <div
            className={classes.stage}
            title={`Stage ${reached + 1} of ${STAGES.length}: ${stageLabel(project)}`}
        >
            <StatusDot color={color}>{stageLabel(project)}</StatusDot>
            <div className={classes.stage__bar} aria-hidden="true">
                {STAGES.map((stage, index) => (
                    <span
                        key={stage}
                        className={clsx(
                            classes.stage__segment,
                            index <= reached && classes["stage__segment--filled"]
                        )}
                    />
                ))}
            </div>
        </div>
    );
};

/** What the deal uses of what it pays for, as a bar: seats active, or consumption against the commitment. */
export const UsageMeasureCell: FC<{ deal: UsageDeal; detail?: boolean; stacked?: boolean }> = ({
    deal,
    detail,
    stacked,
}) => {
    if (deal.usageStatus === "not_connected") return <span className={classes.muted}>–</span>;
    if (deal.type === "consumption") {
        if (deal.pacePct == null) return <span className={classes.muted}>–</span>;
        // Consumption against the commitment: past 100% the bar is full and red.
        return (
            <UtilizationBar
                percent={deal.pacePct}
                detail={detail ? deal.usageDetail : undefined}
                stacked={stacked}
            />
        );
    }
    if (deal.utilizationPct == null) return <span className={classes.muted}>–</span>;
    return (
        <UtilizationBar
            percent={deal.utilizationPct}
            detail={detail ? deal.usageDetail : undefined}
            stacked={stacked}
        />
    );
};

/**
 * A utilization and its bar, colored by its score: inline, or stacked in the
 * savings' rows (the percentage over the bar) as the project card shows it.
 */
export const UtilizationBar: FC<{ percent: number; detail?: string; stacked?: boolean }> = ({
    percent,
    detail,
    stacked,
}) =>
    stacked ? (
        <MeasureProgress value={pct(percent)} percent={percent} color={utilizationTier(percent)} />
    ) : (
        <span className={classes.measure}>
            <span className={classes.measure__track} aria-hidden="true">
                <span
                    className={clsx(
                        classes.measure__fill,
                        classes[`measure__fill--${utilizationTier(percent)}`]
                    )}
                    style={{ width: `${Math.min(100, percent)}%` }}
                />
            </span>
            <span className={classes.measure__value}>{pct(percent)}</span>
            {detail && <span className={classes.measure__detail}>{detail}</span>}
        </span>
    );

const STATUS_COLOR: Record<DealStatus, StatusDotColor> = {
    project_open: "blue",
    analysis_available: "green",
    not_analyzed: "grey",
    not_connected: "orange",
};

/**
 * The deal's status. An open project links to it; a deal without usage
 * offers to request an analysis, which creates a project waiting on us.
 */
export const DealStatusCell: FC<{
    status: DealStatus;
    projectId?: string;
    /** The open project itself: its stage reads as the status, linked to it unless `linked` is false. */
    project?: UsageProject;
    linked?: boolean;
    requested?: boolean;
    onRequest?: () => void;
}> = ({ status, projectId, project, linked = true, requested, onRequest }) => {
    if (project) {
        // Only the demo's drillable project opens; the others say so on hover.
        return (
            <StatusDot color={STAGE_COLOR[project.stage]}>
                {!linked ? (
                    stageLabel(project)
                ) : isDrillableProject(project.id) ? (
                    <Link to={usageProjectPath(project.id)}>{stageLabel(project)}</Link>
                ) : (
                    <NotInDemo>{stageLabel(project)}</NotInDemo>
                )}
            </StatusDot>
        );
    }
    if (status === "not_connected" && onRequest) {
        return (
            <span className={classes.measure}>
                <StatusDot color={STATUS_COLOR[status]}>{DEAL_STATUS_LABEL[status]}</StatusDot>
                <Button size="small" onClick={onRequest} disabled={requested}>
                    {requested ? "Requested" : "Request usage analysis"}
                </Button>
            </span>
        );
    }
    const label =
        status === "project_open" && projectId && isDrillableProject(projectId) ? (
            <Link to={usageProjectPath(projectId)}>{DEAL_STATUS_LABEL[status]}</Link>
        ) : status === "project_open" ? (
            <NotInDemo>{DEAL_STATUS_LABEL[status]}</NotInDemo>
        ) : (
            DEAL_STATUS_LABEL[status]
        );
    return <StatusDot color={STATUS_COLOR[status]}>{label}</StatusDot>;
};

/** "Microsoft 365 usage export, 25 Oct 2026; 94% of users matched to the contract." */
export const coverageText = (coverage: Coverage): string => {
    const parts = [`${coverage.source}, ${longDate(coverage.asOf)}`];
    if (coverage.matchedPct != null)
        parts.push(`${coverage.matchedPct}% of users matched to the contract`);
    if (coverage.note) parts.push(coverage.note);
    return parts.join("; ") + ".";
};

export const CoverageLine: FC<{ coverage?: Coverage; className?: string }> = ({
    coverage,
    className,
}) =>
    coverage ? (
        <div className={clsx(classes.coverage, className)}>{coverageText(coverage)}</div>
    ) : null;

export const NOT_IN_DEMO = "Detail not part of this demo";

/** Wraps what a real product would make clickable but the demo does not drill into. */
export const NotInDemo: FC<{ children: ReactNode; link?: boolean }> = ({ children, link }) => (
    <Tooltip title={NOT_IN_DEMO} mouseEnterDelay={0.3}>
        <span className={clsx(classes.inert, link && classes["inert--link"])}>{children}</span>
    </Tooltip>
);

export const NewTag: FC = () => <Tag className={classes.new}>New</Tag>;

/** A vendor's initial on its colour, as the hub and vendor pages draw it. */
export const vendorFallback = (vendor: UsageVendor) => {
    const letter = vendor.name.charAt(0).toUpperCase();
    return { letter, color: getColorFromLetter(letter) };
};

/**
 * The first-column cell of a project row, as the hub draws it: the vendor's
 * logo, the project's name as the link, the vendor under it.
 */
export const ProjectEntityCell: FC<{
    project: UsageProject;
    vendor: UsageVendor;
    href?: string;
    extra?: ReactNode;
}> = ({ project, vendor, href, extra }) => {
    const { letter, color } = vendorFallback(vendor);
    const name = href ? (
        <Link to={href}>{project.name}</Link>
    ) : (
        <NotInDemo>{project.name}</NotInDemo>
    );
    return (
        <DataTableEntityCell
            imageSrc={vendor.hero}
            imageFallback={letter}
            imageFallbackColor={color}
            name={
                <>
                    {name}
                    {extra}
                </>
            }
            detail={vendor.name}
        />
    );
};

/**
 * A row's name and, under it, a detail line, for the tree's group and deal
 * rows. `indent` is the deal row's step, or a width in pixels when the text
 * must line up with something above it (a project's name after its logo).
 */
export const NameCell: FC<{
    name: ReactNode;
    detail?: ReactNode;
    /** The name's weight: semibold by default, "medium" a step down, "plain" the normal weight. */
    weight?: "medium" | "plain";
    indent?: boolean | number;
}> = ({ name, detail, weight, indent }) => (
    <span
        className={clsx(
            classes.node,
            detail && classes["node--two-line"],
            indent === true && classes["node--deal"]
        )}
        style={typeof indent === "number" ? { paddingLeft: indent } : undefined}
    >
        <span className={clsx(classes.node__name, weight && classes[`node__name--${weight}`])}>
            {name}
        </span>
        {detail && <span className={classes.node__detail}>{detail}</span>}
    </span>
);

/**
 * An action item's status. An open item is in progress when it carries a
 * note (what is being done), not started otherwise.
 */
export const ActionItemStatus: FC<{ item: ActionItem }> = ({ item }) => {
    if (item.status === "done") return <StatusDot color="green">Done</StatusDot>;
    if (item.status === "canceled") return <StatusDot color="grey">Canceled</StatusDot>;
    if (item.note) return <StatusDot color="blue">{item.note}</StatusDot>;
    return <StatusDot color="grey">Not started</StatusDot>;
};

/**
 * The actions on a user's action item: a reminder and marking it done while
 * open (the check can be left out when a platform button does the job);
 * once done, a button that reopens it. InfinityLoop's items and canceled
 * ones have none.
 */
export const ActionItemActions: FC<{ item: ActionItem; markDone?: boolean }> = ({
    item,
    markDone = true,
}) => {
    if (item.status === "canceled" || item.ownerKind === "infinityloop") return null;
    if (item.status === "done") {
        return (
            <Button size="small" onClick={() => setItemStatus(item.id, "open")}>
                Reopen
            </Button>
        );
    }
    return (
        <DataTableActions>
            <DataTableActionButton
                icon="calendar"
                label="Remind me"
                color="grey"
                onClick={() => message.success(`Reminder set: ${item.title}`)}
            />
            {markDone && (
                <DataTableActionButton
                    icon="check"
                    label="Mark done"
                    color="grey"
                    onClick={() => {
                        setItemStatus(item.id, "done");
                        message.success(`Done: ${item.title}`);
                    }}
                />
            )}
        </DataTableActions>
    );
};
